import React from 'react';
import { Shield, Award, CheckCircle, AlertTriangle } from 'lucide-react';

const SafetyPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero */}
      <div className="bg-gradient-to-r from-gray-950 to-black py-20 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">
            Safety <span className="text-yellow-400">Guidelines</span>
          </h1>
          <p className="text-gray-400 text-lg">Your safety is our top priority</p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* SANZ Standards */}
            <section className="bg-gradient-to-br from-yellow-400/10 to-yellow-900/10 border border-yellow-400/20 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Award className="text-yellow-400 flex-shrink-0 mt-1" size={40} />
                <div>
                  <h2 className="text-3xl font-black text-white mb-2">SANZ Standards Compliance</h2>
                  <p className="text-gray-300">
                    We follow the <strong className="text-white">Standards of Australia and New Zealand (SANZ)</strong> for all our bungee jumping operations, ensuring world-class safety protocols.
                  </p>
                </div>
              </div>
            </section>

            {/* Safety Equipment */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Shield className="text-yellow-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Premium Safety Equipment</h2>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="text-white font-bold">Bungee Cords</h3>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li className="flex items-start">
                      <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Imported from New Zealand</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Regularly tested and replaced</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Weight-specific cord selection</span>
                    </li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="text-white font-bold">Harness System</h3>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li className="flex items-start">
                      <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Full-body safety harness</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Ankle and body attachments</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>Double-checked before every jump</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Pre-Jump Process */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Pre-Jump Safety Process</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Medical Screening</h3>
                    <p className="text-gray-300 text-sm">Complete health declaration and verification of medical eligibility</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Weight Check</h3>
                    <p className="text-gray-300 text-sm">Precise weight measurement to select appropriate equipment</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Safety Briefing</h3>
                    <p className="text-gray-300 text-sm">Detailed instructions on jump position, signals, and procedures</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Equipment Fitting</h3>
                    <p className="text-gray-300 text-sm">Professional fitting of harness and safety gear</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">5</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Final Check</h3>
                    <p className="text-gray-300 text-sm">Jump master performs final equipment and readiness verification</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Certified Team */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Certified Jump Masters</h2>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>Internationally certified jump masters</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>Minimum 5 years of experience</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>Regular training and certification updates</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>First aid and emergency response trained</span>
                </li>
              </ul>
            </section>

            {/* Emergency Procedures */}
            <section className="bg-red-500/10 border border-red-500/20 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <AlertTriangle className="text-red-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Emergency Procedures</h2>
                </div>
              </div>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">•</span>
                  <span>On-site medical team and first aid equipment</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">•</span>
                  <span>Emergency evacuation procedures in place</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">•</span>
                  <span>Direct communication with local hospitals</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">•</span>
                  <span>Backup systems for all critical equipment</span>
                </li>
              </ul>
            </section>

            {/* Safety Record */}
            <section className="bg-green-500/10 border border-green-500/20 rounded-xl p-8 text-center">
              <div className="text-7xl font-black text-green-400 mb-4">100%</div>
              <h2 className="text-2xl font-bold text-white mb-2">Perfect Safety Record</h2>
              <p className="text-gray-300">
                With over 44,000 jumps completed, we maintain a flawless safety record with zero major incidents.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyPage;